import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, AlertTriangle } from "lucide-react";
import { fmtRM } from "@/pages/hr/payroll/payrollUtils";

export default function PayrollProfilesTab({ employees = [] }) {
  const { can } = useAuth();
  const canManage = can("payroll:manage_profiles");
  const canAmounts = can("hr:view_sensitive");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/hr/payroll/profiles").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="payroll-profiles-tab">
      <div className="rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 p-2.5 mb-3">
        Basic Salary is <b>effective-dated</b> — a salary increment never overwrites history. Finalized payroll snapshots the resolved value, so later changes never alter past payroll.
        {!canAmounts && <span className="text-amber-700"> · Salary & bank amounts hidden (requires HR: View Sensitive).</span>}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="payroll-profiles-table">
          <thead><tr><th>Employee</th><th>Status</th><th>Payroll Eligible</th><th>Current Basic</th><th>Eff. From</th><th>Setup</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const missing = [];
              if (!r.has_profile) missing.push("No Profile");
              else if (r.payroll_eligible && r.current_basic_salary === null && !r.salary_amounts_hidden) missing.push("No Basic Salary");
              return (
                <tr key={r.employee_id} data-testid={`profile-row-${r.employee_id}`}>
                  <td><span className="font-mono text-xs">{r.employee_id}</span> {r.full_name}</td>
                  <td className="text-xs">{r.employment_status}</td>
                  <td className="text-center">{r.payroll_eligible ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Yes</Badge> : <Badge variant="outline" className="text-slate-500">No</Badge>}</td>
                  <td>{r.salary_amounts_hidden ? "•••" : fmtRM(r.current_basic_salary)}</td>
                  <td className="text-xs">{r.current_salary_effective_from || "—"}</td>
                  <td>{missing.length ? <Badge variant="outline" className="text-amber-700 border-amber-300"><AlertTriangle className="w-3 h-3 mr-1" />{missing.join(", ")}</Badge> : <Badge variant="outline" className="text-emerald-700 border-emerald-300">OK</Badge>}</td>
                  <td className="text-right">{canManage && <Button size="sm" variant="outline" data-testid={`profile-edit-${r.employee_id}`} onClick={() => setEdit(r)}><UserCog className="w-3.5 h-3.5 mr-1" />Setup</Button>}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-6">No employees.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <ProfileEditor emp={edit} canAmounts={canAmounts} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}

function ProfileEditor({ emp, canAmounts, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [salForm, setSalForm] = useState({ basic_salary: "", effective_from: "", effective_to: "", note: "" });
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get(`/hr/payroll/profiles/${emp.employee_id}`).then((r) => setP(r.data || defaultProfile())).catch(() => setP(defaultProfile()));
    if (canAmounts) api.get(`/hr/payroll/profiles/${emp.employee_id}/salaries`).then((r) => setSalaries(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  function defaultProfile() {
    return { payroll_eligible: true, employment_type: "full_time", payment_method: "bank", epf_applicable: true,
      epf_setting: "statutory", socso_applicable: true, eis_applicable: true, pcb_applicable: true, pcb_info: {} };
  }
  const set = (k, v) => setP({ ...p, [k]: v });
  const setPcb = (k, v) => setP({ ...p, pcb_info: { ...(p.pcb_info || {}), [k]: v } });

  const saveProfile = async () => {
    setBusy(true);
    try {
      const body = { ...p };
      ["epf_employee_rate", "epf_employer_rate", "pcb_manual_amount"].forEach((k) => { if (body[k] === "" ) body[k] = null; else if (body[k] != null) body[k] = Number(body[k]); });
      await api.put(`/hr/payroll/profiles/${emp.employee_id}`, body);
      toast.success("Profile saved"); onSaved(); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addSalary = async () => {
    if (!salForm.basic_salary || !salForm.effective_from) return toast.error("Basic salary and effective date required");
    setBusy(true);
    try {
      await api.post(`/hr/payroll/profiles/${emp.employee_id}/salaries`, { basic_salary: Number(salForm.basic_salary), effective_from: salForm.effective_from, effective_to: salForm.effective_to || null, note: salForm.note });
      toast.success("Salary added"); setSalForm({ basic_salary: "", effective_from: "", effective_to: "", note: "" }); loadAll(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const delSalary = async (id) => { try { await api.delete(`/hr/payroll/salaries/${id}`); toast.success("Removed"); loadAll(); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!p) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="profile-editor-dialog">
        <DialogHeader><DialogTitle>Payroll Profile — {emp.employee_id} · {emp.full_name}</DialogTitle>
          <DialogDescription>Eligibility, statutory configuration & effective-dated salary.</DialogDescription></DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-3">
            <Switch checked={!!p.payroll_eligible} onCheckedChange={(v) => set("payroll_eligible", v)} data-testid="profile-eligible-switch" />
            <Label>Payroll Eligible</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Employment Type</Label><Input value={p.employment_type || ""} onChange={(e) => set("employment_type", e.target.value)} data-testid="profile-employment-type" /></div>
            <div><Label className="text-xs">Payment Method</Label>
              <Select value={p.payment_method || "bank"} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger data-testid="profile-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
              </Select></div>
          </div>
          {canAmounts && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Bank Code</Label><Input value={p.bank_code || ""} onChange={(e) => set("bank_code", e.target.value)} data-testid="profile-bank-code" /></div>
              <div><Label className="text-xs">Bank Account No</Label><Input value={p.bank_account_no || ""} onChange={(e) => set("bank_account_no", e.target.value)} data-testid="profile-bank-acct" /></div>
              <div><Label className="text-xs">Beneficiary</Label><Input value={p.bank_beneficiary || ""} onChange={(e) => set("bank_beneficiary", e.target.value)} /></div>
            </div>
          )}

          <div className="rounded-md border border-slate-200 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-slate-500">Statutory Configuration</p>
            <div className="grid grid-cols-4 gap-2">
              <Toggle label="EPF" v={p.epf_applicable} on={(v) => set("epf_applicable", v)} tid="profile-epf-applicable" />
              <Toggle label="SOCSO" v={p.socso_applicable} on={(v) => set("socso_applicable", v)} tid="profile-socso-applicable" />
              <Toggle label="EIS" v={p.eis_applicable} on={(v) => set("eis_applicable", v)} tid="profile-eis-applicable" />
              <Toggle label="PCB" v={p.pcb_applicable} on={(v) => set("pcb_applicable", v)} tid="profile-pcb-applicable" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">EPF No</Label><Input value={p.epf_no || ""} onChange={(e) => set("epf_no", e.target.value)} data-testid="profile-epf-no" /></div>
              <div><Label className="text-xs">SOCSO No</Label><Input value={p.socso_no || ""} onChange={(e) => set("socso_no", e.target.value)} data-testid="profile-socso-no" /></div>
              <div><Label className="text-xs">Tax No (LHDN)</Label><Input value={p.tax_no || ""} onChange={(e) => set("tax_no", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2 items-end">
              <div><Label className="text-xs">EPF Setting</Label>
                <Select value={p.epf_setting || "statutory"} onValueChange={(v) => set("epf_setting", v)}>
                  <SelectTrigger data-testid="profile-epf-setting"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="statutory">Default / Statutory (KWSP Third Schedule)</SelectItem><SelectItem value="override">Employee-specific Override</SelectItem></SelectContent>
                </Select></div>
              {p.epf_setting === "override" && canAmounts && <>
                <div><Label className="text-xs">EE Rate (e.g. 0.11)</Label><Input type="number" step="0.01" value={p.epf_employee_rate ?? ""} onChange={(e) => set("epf_employee_rate", e.target.value)} data-testid="profile-epf-ee-rate" /></div>
                <div><Label className="text-xs">ER Rate (e.g. 0.13)</Label><Input type="number" step="0.01" value={p.epf_employer_rate ?? ""} onChange={(e) => set("epf_employer_rate", e.target.value)} data-testid="profile-epf-er-rate" /></div>
              </>}
            </div>
          </div>

          <div className="rounded-md border border-slate-200 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase text-slate-500">PCB / LHDN Tax Info <span className="normal-case text-amber-700">(pending full LHDN engine — 2027)</span></p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Marital Status</Label>
                <Select value={p.pcb_info?.marital_status || "single"} onValueChange={(v) => setPcb("marital_status", v)}>
                  <SelectTrigger data-testid="profile-marital"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs">Spouse Working</Label>
                <Select value={String(p.pcb_info?.spouse_working ?? true)} onValueChange={(v) => setPcb("spouse_working", v === "true")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs"># Children</Label><Input type="number" value={p.pcb_info?.num_children ?? 0} onChange={(e) => setPcb("num_children", Number(e.target.value))} data-testid="profile-children" /></div>
            </div>
            {canAmounts && <div><Label className="text-xs">Manual PCB Amount (optional — e.g. from AutoCount)</Label><Input type="number" step="0.01" value={p.pcb_manual_amount ?? ""} onChange={(e) => set("pcb_manual_amount", e.target.value)} data-testid="profile-pcb-manual" /></div>}
          </div>

          {canAmounts && (
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Basic Salary History (effective-dated)</p>
              <table className="excel-table w-full mb-2" data-testid="salary-history-table">
                <thead><tr><th>Basic</th><th>Effective From</th><th>Effective To</th><th>Note</th><th></th></tr></thead>
                <tbody>
                  {salaries.map((s) => (
                    <tr key={s.id} data-testid={`salary-row-${s.id}`}><td>{fmtRM(s.basic_salary)}</td><td className="text-xs">{s.effective_from}</td><td className="text-xs">{s.effective_to || "—"}</td><td className="text-xs">{s.note}</td>
                      <td className="text-right"><button onClick={() => delSalary(s.id)} data-testid={`salary-del-${s.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button></td></tr>
                  ))}
                  {salaries.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-3">No salary records.</td></tr>}
                </tbody>
              </table>
              <div className="flex items-end gap-2">
                <div><Label className="text-xs">Basic (RM)</Label><Input type="number" step="0.01" className="h-8 w-28" value={salForm.basic_salary} onChange={(e) => setSalForm({ ...salForm, basic_salary: e.target.value })} data-testid="salary-amount-input" /></div>
                <div><Label className="text-xs">Effective From</Label><Input type="date" className="h-8" value={salForm.effective_from} onChange={(e) => setSalForm({ ...salForm, effective_from: e.target.value })} data-testid="salary-from-input" /></div>
                <div><Label className="text-xs">Effective To</Label><Input type="date" className="h-8" value={salForm.effective_to} onChange={(e) => setSalForm({ ...salForm, effective_to: e.target.value })} /></div>
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700 h-8" disabled={busy} onClick={addSalary} data-testid="salary-add-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button><Button className="bg-slate-900" disabled={busy} onClick={saveProfile} data-testid="profile-save-btn">Save Profile</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Toggle = ({ label, v, on, tid }) => (
  <div className="flex items-center gap-2"><Switch checked={!!v} onCheckedChange={on} data-testid={tid} /><Label className="text-xs">{label}</Label></div>
);
